import { Injectable } from "@angular/core";
import { defer, Observable, of as observableOf, of } from "rxjs";
import { VerificationApplication } from "@models";
import { catchError, map, shareReplay } from "rxjs/operators";
import { ErrorHandler, Helper } from "@helpers/functions";
import { KYC_PROVIDER_ONFIDO, KYC_PROVIDER_SUMSUB, KYC_PROVIDER_SUMSUBVIDEO } from "@helpers/constants";
import { OnfidoService } from "@modules/onfido/services/onfido.service";
import { SumsubService } from "@modules/kyc/services/sumsub.service";
import { VerificationModalRemote } from "@modules/verification/modal/infrastructure/verification-modal.remote";
import { CountryDocument } from "../entities/country-documents.model";

@Injectable({ providedIn: "root" })
export class VerificationModalFacade {
	public _providerModalTitle: string = "";
	public _verificationApplications: VerificationApplication[] = null;
	public _selectedVerification: VerificationApplication = null;
	public _documents: { [key: string]: string[] } = null;
	private _loadingDocumentsInProgress: string = "";
	private _currentLoadRequest: Observable<CountryDocument[]> = null;
	private _verificationMethods: { [key: string]: string[] } = null;
	private _verificationCountriesEID: { [key: string]: EIDdata } = null;

	public constructor(
		private verificationModalRemote: VerificationModalRemote,
		private onfidoService: OnfidoService,
		private sumsubService: SumsubService
	) {}

	public getDocumentsByCountry(countryId: string): string[] {
		if (!this._documents || !Object.keys(this._documents).includes(countryId)) {
			this.loadDocuments(countryId).subscribe();
			return null;
		}
		return this._documents[countryId];
	}

	public getVerificationMethods(countryId: string): string[] {
		try {
			return this._verificationMethods[countryId];
		} catch (e) {
			console.error(e);
			return [];
		}
	}

	public getVerificationCountriesEID(countryId: string): EIDdata {
		try {
			return this._verificationCountriesEID[countryId];
		} catch (e) {
			console.error(e);
			return {
				passport_id: 0,
				idcard_id: 0,
				driving_licence: 0
			};
		}
	}

	public loadDocuments(countryId: string): Observable<string[]> {
		if (!this._documents) {
			this._documents = {};
			this._verificationMethods = {};
			this._verificationCountriesEID = {};
		}
		if (!countryId) {
			return of(null);
		}
		return defer(() => {
			if (!this._documents[countryId]) {
				if (this._loadingDocumentsInProgress !== countryId) {
					this._currentLoadRequest = this.verificationModalRemote
						.getCitizenshipDocuments$(countryId)
						.pipe(shareReplay(1)); // reuse the get request on subsequent calls

					this._loadingDocumentsInProgress = countryId;
				}
				return this._currentLoadRequest.pipe(
					map((result) => {
						if (result.length) {
							const country = result[0];
							const attributes = country.attributes;
							const verificationProviders: string[] = [];
							const acceptedDocuments: EIDdata = new EIDdata();
							// eslint-disable-next-line guard-for-in
							for (const provider in attributes) {
								for (const doc in attributes[provider]) {
									if (
										typeof attributes[provider][doc] !== "number" &&
										attributes[provider][doc] &&
										!verificationProviders.includes(doc)
									) {
										verificationProviders.push(doc);
									}
								}
							}
							if (attributes["electronicid"]) {
								acceptedDocuments.passport_id = attributes["electronicid"]["passport_id"];
								acceptedDocuments.idcard_id = attributes["electronicid"]["id_card_id"];
								acceptedDocuments.driving_licence = attributes["electronicid"]["driving_license"];
							}
							this._verificationMethods[country.id] = verificationProviders;
							this._verificationCountriesEID[country.id] = acceptedDocuments;
							const details = [...verificationProviders, { electronicId: acceptedDocuments }];
							this._documents[country.id] = details as string[];
						} else {
							this._documents[countryId] = null;
						}
						this._loadingDocumentsInProgress = "";
						return this._documents[countryId];
					}),
					catchError(() => {
						this._documents[countryId] = null;
						this._loadingDocumentsInProgress = "";
						return [];
					})
				);
			}
			return of(this._documents[countryId]);
		});
	}

	public grabVerifyProviders(
		country_id: string,
		document: string,
		language: string,
		forceReload: boolean = false
		// TODO: eslint fix
		/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
	): Observable<any> {
		if (this._verificationApplications == null || forceReload) {
			return this.verificationModalRemote.getVerificationMethods$(document, country_id, language).pipe(
				map((a: VerificationApplication[]) => {
					this._verificationApplications = a;
					if (this._verificationApplications.length > 0) {
						this._providerModalTitle = "choose_provider_verification";
					} else {
						this._providerModalTitle = "no_provider_available_for_language";
					}

					return this._verificationApplications;
				}),
				catchError((error) => {
					Helper.handleError(error, null, {
						toastrConfig: { timeOut: 3000 }
					} as ErrorHandler);
					return observableOf([]);
				})
			);
		}

		return observableOf(false);
	}

	// eslint-disable-next-line @typescript-eslint/no-empty-function
	public startVerification(closure: Function = () => {}, lang: string): void {
		if (this.startableVerification()) {
			this.openVerification(lang);
			closure();
		} else {
			this.verificationModalRemote.startVerification$(this._selectedVerification, lang).subscribe(
				(a: VerificationApplication[]) => {
					this._selectedVerification = a[0];
					this.openVerification(lang);
					closure();
				},
				(e) => Helper.handleError(e)
			);
		}
	}

	public openVerification(language: string = "eng"): boolean {
		if (this._selectedVerification == null) {
			return false;
		}
		if (this._selectedVerification.getName() === KYC_PROVIDER_ONFIDO && this.onfidoService.getHasEverStarted()) {
			this.onfidoService.initOnfido();
			return true;
		}
		if (
			this._selectedVerification.getName() === KYC_PROVIDER_SUMSUB ||
			this._selectedVerification.getName() === KYC_PROVIDER_SUMSUBVIDEO
		) {
			this.sumsubService.init(this._selectedVerification.getToken(), language);
		}
		if (this._selectedVerification.getUrl() && this._selectedVerification.getUrl().length > 0) {
			window.open(this._selectedVerification.getUrl(), "_blank");
			return true;
		}
		return false;
	}

	private startableVerification(): boolean {
		if (this._selectedVerification != null) {
			return this._selectedVerification.getUrl().length > 0;
		}
		return false;
	}
}

class EIDdata {
	public passport_id: number = 0;
	public idcard_id: number = 0;
	public driving_licence: number = 0;
}
